import React, { memo, useEffect, useState } from 'react'
import map from 'lodash/map'
import Block from 'components/Owner/Tools/Block'
import Loader from 'components/Loaders'
import { isEmpty } from 'lodash'
import { useAuthentication } from 'utils/authentication'
import { GroupedTools, Tool } from '__generated__/api-types-and-hooks'
import { ToolConnectionInputFormModal } from './ToolConnectionInputFormModal'
import { useFlashParams } from 'hooks/useFlashParams'
import { ReduxAction } from 'store/types'
import ToolsWrapper from './ToolsWrapper'
import BlockEmptyState from './BlockEmptyState'

interface IConnectedToolsProps {
  myTools: GroupedTools[]
  oAuthUrl: string
  myToolsLoading: boolean
  getOAuthUrlAction
  getMyToolsAction
  resetOAuthUrlAction
  toolConnectionSuccessAction: ReduxAction<{ playId?: string; connectorName?: string }>
  getUserConnectorsAction
}
const ConnectedTools = ({
  myTools,
  oAuthUrl,
  myToolsLoading,
  getOAuthUrlAction,
  getMyToolsAction,
  resetOAuthUrlAction,
  toolConnectionSuccessAction,
  getUserConnectorsAction,
}: IConnectedToolsProps) => {
  const [activeConnectingTool, setActiveConnectingTool] = useState<Tool | null>(null)
  const [isInputPropertiesModalOpen, setInputPropertiesModalOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { flashParams, removeFlashParams } = useFlashParams<{ connectorName: string }>()
  useEffect(() => {
    if (flashParams?.connectorName) {
      toolConnectionSuccessAction({ connectorName: flashParams.connectorName })
      removeFlashParams(['connectorName'])
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flashParams?.connectorName])

  useEffect(() => {
    getMyToolsAction()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (activeConnectingTool?.options?.connectionForm?.inputProperties?.length) {
      setInputPropertiesModalOpen(true)
    }
  }, [activeConnectingTool, isInputPropertiesModalOpen])

  useAuthentication(oAuthUrl, getUserConnectorsAction, resetOAuthUrlAction)

  return (
    <ToolsWrapper label="My" isItemsGrouped={true}>
      {myToolsLoading ? (
        <Loader loader={'ConnectedTools'} />
      ) : !myToolsLoading && isEmpty(myTools) ? (
        <p className="font-primary font-bold text-lg ">
          No tools found. Add a new tool by completing a play or connecting a tool in the All Tools
          tab.
        </p>
      ) : (
        map(myTools, (groupedTool: GroupedTools, index) => (
          <div className="mb-14" key={index}>
            <h3 className="text-3xl font-light  mb-4">{groupedTool.category}</h3>
            <div className="flex xs:items-start xs:justify-center sm:justify-start xs:gap-6 sm:gap-12 lg:gap-10 xs:flex-wrap min-h-[320px] overflow-y-auto pb-4">
              {groupedTool.tools?.length! > 0 ? (
                <>
                  {groupedTool.tools?.map(
                    (tool) =>
                      tool?.visible && (
                        <Block
                          tool={tool!}
                          key={tool?.id}
                          setActiveConnectingTool={setActiveConnectingTool}
                          getOAuthUrlAction={getOAuthUrlAction}
                          onDisconnect={() => getMyToolsAction()}
                        />
                      )
                  )}
                </>
              ) : (
                <BlockEmptyState category={groupedTool.category!} />
              )}
            </div>
          </div>
        ))
      )}
      {isInputPropertiesModalOpen && activeConnectingTool ? (
        <ToolConnectionInputFormModal
          tool={activeConnectingTool}
          onClose={() => {
            setInputPropertiesModalOpen(false)
            setActiveConnectingTool(null)
            getMyToolsAction()
          }}
          getOAuthUrlAction={getOAuthUrlAction}
          oAuthUrl={oAuthUrl}
        />
      ) : (
        <></>
      )}
    </ToolsWrapper>
  )
}

export default memo(ConnectedTools)
